import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { InputText } from 'primereact/inputtext';
import { Accordion, AccordionTab } from 'primereact/accordion';
import classes from '../SelectContractType/selectContractType.module.scss';
import { useStore } from 'hooks/useStore';
import { OpportunityIteration, StrengthWithPackSize } from 'services/bidAnalysis/bidAnalysisServices';

// Returns a JSX element representing the strengths for a given iteration.
// Each strength is shown as a label followed by pill chips for each pack size.
// Duplicate pack sizes are removed before rendering.
const renderStrengths = (strengths: StrengthWithPackSize[] | undefined): JSX.Element => {
  if (!strengths || strengths.length === 0) {
    return <span>—</span>;
  }
  return (
    <div className={classes.strengthsCell}>
      {strengths.map((entry, i) => {
        const uniquePacks = [...new Set(entry.PackSize.filter(p => p !== ''))];
        return (
          <div key={i} className={classes.strengthRow}>
            <span className={classes.strengthLabel}>{entry.Strength}</span>
            {uniquePacks.map((pack, j) => (
              <span key={j} className={classes.packSizeChip}>{pack}</span>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const SelectOpportunity = observer(() => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { BidAnalysisStore } = useStore();
  const [searchQuery, setSearchQuery] = useState('');
  // Indices of open accordion panels (multiple can be open simultaneously)
  const [activeIndex, setActiveIndex] = useState<number | number[]>([]);

  const contractTypeFromUrl = searchParams.get('type') as 'mas' | 'nc' | null;

  useEffect(() => {
    if (contractTypeFromUrl && !BidAnalysisStore.selectedContractType) {
      BidAnalysisStore.setContractType(contractTypeFromUrl);
    }

    if (!BidAnalysisStore.selectedContractType && !contractTypeFromUrl) {
      console.error('No contract type selected, redirecting to step 1');
      navigate('/bdportal/bid-analysis/wizard/contract-type');
      return;
    }

    const contractType = BidAnalysisStore.selectedContractType || contractTypeFromUrl;

    if (contractType) {
      const hasDataForContractType =
        BidAnalysisStore.opportunityIterations.length > 0 &&
        BidAnalysisStore.opportunityIterationsContractType === contractType;

      if (hasDataForContractType) {
        if (BidAnalysisStore.error) {
          BidAnalysisStore.clearError();
        }
      } else if (!BidAnalysisStore.isLoadingOpportunities) {
        if (BidAnalysisStore.error) {
          BidAnalysisStore.clearError();
        }
        BidAnalysisStore.fetchOpportunityIterations(contractType);
      }
    }

    // Restore selected opportunity from URL
    const oppIdFromUrl = searchParams.get('oppId');
    if (oppIdFromUrl && BidAnalysisStore.opportunityIterations.length > 0) {
      const opportunity = BidAnalysisStore.opportunityIterations.find(
        (opp) => opp.id.toString() === oppIdFromUrl
      );
      if (opportunity && !BidAnalysisStore.selectedOpportunity) {
        BidAnalysisStore.setSelectedOpportunity(opportunity);
      }
    }
  }, [contractTypeFromUrl, searchParams, BidAnalysisStore, navigate]);

  // Filter opportunities by: molecule name, OID, primary partner, source, strength values
  const filteredOpportunities = useMemo(() => {
    if (!searchQuery.trim()) {
      return BidAnalysisStore.opportunityIterations;
    }
    const query = searchQuery.toLowerCase();
    return BidAnalysisStore.opportunityIterations.filter((opp) => {
      if (opp.opportunityName?.toLowerCase().includes(query)) return true;
      if (opp.id?.toString().includes(query)) return true;
      if (opp.primarySupplierId?.toLowerCase().includes(query)) return true;
      if (opp.source?.toLowerCase().includes(query)) return true;
      if (opp.strengths?.some(s => s.Strength.toLowerCase().includes(query))) return true;
      return false;
    });
  }, [BidAnalysisStore.opportunityIterations, searchQuery]);

  // Group filtered opportunities by MoleculeAndForm (opportunityName)
  const groupedOpportunities = useMemo(() => {
    const map = new Map<string, OpportunityIteration[]>();
    for (const opp of filteredOpportunities) {
      const key = opp.opportunityName || 'Unknown';
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(opp);
    }
    return Array.from(map.entries()).map(([moleculeAndForm, iterations]) => ({
      moleculeAndForm,
      iterations,
    }));
  }, [filteredOpportunities]);

  // Auto-expand: all groups when searching, or the group containing the selected OID when restoring from URL
  useEffect(() => {
    if (searchQuery.trim()) {
      // Open all groups so the user sees all matching results immediately
      setActiveIndex(groupedOpportunities.map((_, i) => i));
      return;
    }

    // When search is cleared, collapse all — unless there is a selected opportunity from URL
    const oppIdFromUrl = searchParams.get('oppId');
    if (oppIdFromUrl && groupedOpportunities.length > 0) {
      const targetGroupIndex = groupedOpportunities.findIndex((group) =>
        group.iterations.some((opp) => opp.id.toString() === oppIdFromUrl)
      );
      if (targetGroupIndex !== -1) {
        setActiveIndex([targetGroupIndex]);
        return;
      }
    }

    setActiveIndex([]);
  }, [searchQuery, groupedOpportunities, searchParams]);

  const handleNext = () => {
    if (BidAnalysisStore.selectedOpportunity) {
      const contractType = BidAnalysisStore.selectedContractType || contractTypeFromUrl;
      if (contractType === 'nc') {
        navigate(`/bdportal/bid-analysis/wizard/confirmation?type=${contractType}&oppId=${BidAnalysisStore.selectedOpportunity.id}`);
      } else {
        navigate(`/bdportal/bid-analysis/wizard/product-type?type=${contractType}&oppId=${BidAnalysisStore.selectedOpportunity.id}`);
      }
    }
  };

  const handleBack = () => {
    const contractType = BidAnalysisStore.selectedContractType || contractTypeFromUrl;
    navigate(`/bdportal/bid-analysis/wizard/contract-type${contractType ? `?type=${contractType}` : ''}`);
  };

  const handleSelectOpportunity = (opportunity: OpportunityIteration) => {
    BidAnalysisStore.setSelectedOpportunity(opportunity);
    const contractType = BidAnalysisStore.selectedContractType || contractTypeFromUrl;
    setSearchParams({ type: contractType || '', oppId: opportunity.id.toString() });
  };

  const handleOpenOpportunity = (e: React.MouseEvent, opportunity: OpportunityIteration) => {
    e.stopPropagation();
    const url = `/bdportal/optracker/board?opportunityIdParam=${opportunity.opportunityId}&opportunityIterationIdParam=${opportunity.id}`;
    window.open(url, '_blank');
  };

  if (BidAnalysisStore.isLoadingOpportunities) {
    return (
      <div className={classes.loadingContainer}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem', color: '#522d6e' }}></i>
        <p>Loading opportunities...</p>
      </div>
    );
  }

  const contractType = BidAnalysisStore.selectedContractType || contractTypeFromUrl;
  const hasDataForContractType =
    BidAnalysisStore.opportunityIterations.length > 0 &&
    BidAnalysisStore.opportunityIterationsContractType === contractType;

  if (BidAnalysisStore.error && !hasDataForContractType && !BidAnalysisStore.isLoadingOpportunities) {
    return (
      <div className={classes.wizardContainer}>
        <div className={classes.header}>
          <div className={classes.headerContent}>
            <div className={classes.logo}>
              <span>gsms</span>
            </div>
            <h1>Create New Bid Analysis</h1>
          </div>
        </div>
        <div className={classes.contentContainer}>
          <div className={classes.errorContainer}>
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#f59e0b' }}></i>
            <h3>Error Loading Opportunities</h3>
            <p>{BidAnalysisStore.error}</p>
            <Button label="Go Back" icon="pi pi-arrow-left" onClick={handleBack} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.wizardContainer}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headerContent}>
          <div className={classes.logo}>
            <span>gsms</span>
          </div>
          <h1>Create New Bid Analysis</h1>
        </div>
      </div>

      <div className={classes.contentContainer}>
        {/* Progress Stepper */}
        <div className={classes.stepperContainer}>
          <div className={classes.stepper}>
            <div className={classes.stepLine}></div>
            <div
              className={classes.stepLineProgress}
              style={{ width: contractTypeFromUrl === 'nc' ? '66%' : '33%' }}
            ></div>

            <div className={classes.step}>
              <div className={`${classes.stepCircle} ${classes.stepCircleCompleted}`}>
                <i className="pi pi-check"></i>
              </div>
              <div className={classes.stepLabel}>Contract Type</div>
            </div>

            <div className={`${classes.step} ${classes.stepActive}`}>
              <div className={`${classes.stepCircle} ${classes.stepCircleActive}`}>2</div>
              <div className={classes.stepLabel}>Opportunity List</div>
            </div>

            {contractTypeFromUrl !== 'nc' && (
              <>
                <div className={classes.step}>
                  <div className={classes.stepCircle}>3</div>
                  <div className={classes.stepLabel}>Product Type</div>
                </div>
                <div className={classes.step}>
                  <div className={classes.stepCircle}>4</div>
                  <div className={classes.stepLabel}>Confirmation</div>
                </div>
              </>
            )}

            {contractTypeFromUrl === 'nc' && (
              <div className={classes.step}>
                <div className={classes.stepCircle}>3</div>
                <div className={classes.stepLabel}>Confirmation</div>
              </div>
            )}
          </div>
        </div>

        {/* Page Title */}
        <h2 className={classes.pageTitle}>Select Opportunity</h2>

        {/* Search Bar */}
        <div className={classes.searchContainer}>
          <span className={classes.searchIcon}>
            <i className="pi pi-search"></i>
          </span>
          <InputText
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by Molecule, OID, Partner, Source or Strength..."
            className={classes.searchInput}
          />
          {searchQuery && (
            <Button
              icon="pi pi-times"
              className={classes.clearSearchBtn}
              onClick={() => setSearchQuery('')}
              text
              rounded
            />
          )}
        </div>

        {/* Opportunity Accordion */}
        <div className={classes.selectionSection}>
          {filteredOpportunities.length === 0 ? (
            <div className={classes.emptyState}>
              <i className="pi pi-inbox" style={{ fontSize: '3rem', color: '#9ca3af' }}></i>
              <p>
                {searchQuery
                  ? `No opportunities found matching "${searchQuery}"`
                  : 'No opportunities available for the selected contract type.'}
              </p>
              {searchQuery && (
                <Button
                  label="Clear Search"
                  icon="pi pi-times"
                  onClick={() => setSearchQuery('')}
                  outlined
                />
              )}
              {!searchQuery && (
                <Button label="Go Back" icon="pi pi-arrow-left" onClick={handleBack} outlined />
              )}
            </div>
          ) : (
            <Accordion
              multiple
              activeIndex={activeIndex}
              onTabChange={(e) => setActiveIndex(e.index)}
              className={classes.opportunityAccordion}
            >
              {groupedOpportunities.map((group) => (
                <AccordionTab
                  key={group.moleculeAndForm}
                  header={
                    <div className={classes.accordionHeader}>
                      <span>{group.moleculeAndForm}</span>
                      <span className={classes.iterationCount}>
                        ({group.iterations.length})
                      </span>
                    </div>
                  }
                >
                  <div className={classes.opportunityTableContainerInAccordion}>
                    <table className={classes.opportunityTable}>
                      <thead>
                        <tr>
                          <th style={{ width: '48px' }}></th>
                          <th style={{ width: '80px' }}>OID</th>
                          <th>Primary Partner</th>
                          <th>Source</th>
                          <th>Strengths</th>
                          <th style={{ width: '48px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.iterations.map((opportunity) => (
                          <tr
                            key={opportunity.id}
                            className={
                              BidAnalysisStore.selectedOpportunity?.id === opportunity.id
                                ? classes.selectedRow
                                : ''
                            }
                            onClick={() => handleSelectOpportunity(opportunity)}
                          >
                            <td>
                              <RadioButton
                                inputId={`opp-${opportunity.id}`}
                                value={opportunity.id}
                                checked={
                                  BidAnalysisStore.selectedOpportunity?.id === opportunity.id
                                }
                                onChange={() => handleSelectOpportunity(opportunity)}
                              />
                            </td>
                            <td>{opportunity.id}</td>
                            <td>{opportunity.primarySupplierId ?? '—'}</td>
                            <td>{opportunity.source || '—'}</td>
                            <td>{renderStrengths(opportunity.strengths)}</td>
                            <td>
                              <i
                                className="pi pi-external-link"
                                style={{ color: '#667eea', cursor: 'pointer' }}
                                onClick={(e) => handleOpenOpportunity(e, opportunity)}
                                title="Open in Opportunity Tracker"
                              ></i>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </AccordionTab>
              ))}
            </Accordion>
          )}
        </div>

        {/* Action Buttons */}
        <div className={classes.actionButtons}>
          <Button label="Back" icon="pi pi-arrow-left" onClick={handleBack} outlined />
          <Button
            label="Next"
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={handleNext}
            disabled={!BidAnalysisStore.selectedOpportunity}
          />
        </div>
      </div>
    </div>
  );
});

export default SelectOpportunity;
